import React, { useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { RootStateAnswers } from "../../../questionnaire/store/answers-slice";
import InfoIcon from "../../svg/InfoIcon";
import EatingPatternTab from "./tabs/EatingPatternTab";
import FoodPreferencesTab from "./tabs/FoodPreferencesTab";
import EatingHabitsTab from "./tabs/EatingHabitsTab";
import WorkoutFuelsTab from "./tabs/WorkoutFuelsTab";
import { Theme } from "../../../../providers/ThemeProvider";
import { useSubject } from "../../providers/SubjectProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../../hooks/usePlatformResource";
import PageLoader from "../../../../common/ui/PageLoader";
import { NormalizedNutritionV2 } from "./type";

/**
 * The Nutrition Questionnaire view — one scrolling page of four sections.
 *
 * Was four inner tabs behind a SecondaryTabMenuPicker; the redesign stacks them
 * so the whole questionnaire reads top to bottom. The section components are the
 * former tab components, unchanged in what they render.
 */

const SECTIONS: {
  key: string;
  title: string;
  Component: React.ComponentType<{
    data: NormalizedNutritionV2;
    openPopperId: string | null;
    setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
  }>;
}[] = [
  { key: "eating-pattern", title: "Eating Pattern", Component: EatingPatternTab },
  {
    key: "food-preferences",
    title: "Food Preferences",
    Component: FoodPreferencesTab,
  },
  { key: "eating-habits", title: "Eating Habits", Component: EatingHabitsTab },
  { key: "workout-fuels", title: "Workout Fuels", Component: WorkoutFuelsTab },
];

const NutritionTab = () => {
  const theme: Theme = useTheme();
  // One popover open at a time across the whole page, not just one section.
  const [openPopperId, setOpenPopperId] = useState<string | null>(null);
  const { subject, subjectId } = useSubject();
  const { AuthUser } = useAuth();
  const data = useSelector(
    (state: RootStateAnswers) => state.answers.nutritionQuest
  );

  const nutritionStatus = usePlatformResource(
    "nutritionQuestionnaire",
    subjectId,
    AuthUser.role
  );

  // Read the flag off the subject rather than ASSelectedClient: in the
  // customer's own workspace (mode="self") no client is selected, so
  // ASSelectedClient is null and this would always fall through to the
  // empty state. The completed flag can also be stale (e.g. the questionnaire
  // GET 404s), so rendering additionally requires the normalized data.
  const hasData = subject?.nutrition_questionnaire_completed && data !== null;

  // Distinguish "still loading" from "genuinely incomplete" — without this the
  // empty state would flash on every visit before the fetch resolves.
  if (subject?.nutrition_questionnaire_completed && isResourcePending(nutritionStatus)) {
    return <PageLoader />;
  }

  if (!hasData) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.noQuestionnaireContainer(theme)}>
          <InfoIcon color={theme.colorTokens.W[60]} />
          <Box sx={styles.text(theme)}>
            The nutrition questionnaire is incomplete. Please ensure the
            customer completes it to generate their customized diet plan.
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {SECTIONS.map(({ key, title, Component }) => (
        <Box key={key} sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>{title}</Box>
          <Component
            data={data}
            openPopperId={openPopperId}
            setOpenPopperId={setOpenPopperId}
          />
        </Box>
      ))}
    </Box>
  );
};

export default NutritionTab;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  noQuestionnaireContainer: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    padding: "12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    width: "fit-content",
  }),
  text: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
